import type {
  NavbarDocument,
  NavbarSerializableNode,
  SerializableNode,
  TSectionElement,
} from '@shared/dream-components';
import { constructMobileView, NavbarStylesV2, navbarV2Migrator } from '@shared/dream-components';

import type { NodeContent, Page } from '~/interfaces/page';
import type { Site } from '~/interfaces/site';

import { serializeNode as serializePageNode } from '../nodes/serializeNode';
import NavbarRendererV2 from './NavbarRenderer';

interface NavbarSerializerProps {
  site: Site;
  page?: Page;
}

const serializeNode = (
  node: NavbarSerializableNode | NavbarSerializableNode[],
  screen: 'desktop' | 'mobile',
  parent?: NavbarSerializableNode,
) => {
  if (!node) return null;

  if (Array.isArray(node)) {
    return node.filter(Boolean).map((child, index) => (
      <NavbarRendererV2 key={`${index}-${child?.attrs?.id}`} node={child} screen={screen} parent={parent}>
        {'content' in child && Array.isArray(child.content) ? serializeNode(child.content, screen, child) : null}
      </NavbarRendererV2>
    ));
  }

  return (
    <NavbarRendererV2 node={node} screen={screen} parent={parent}>
      {'content' in node && Array.isArray(node.content) ? serializeNode(node.content, screen, node) : null}
    </NavbarRendererV2>
  );
};

// Applies style and visibility settings for user button/dropdown
const applyUserButtonDropdown = (node: NavbarSerializableNode, site: Site) => {
  if (Array.isArray(node)) {
    node.forEach((childNode) => {
      applyUserButtonDropdown(childNode, site);
    });
    return;
  }

  // Handle User Button Dropdown Defaults
  if (
    node.attrs &&
    'type' in node.attrs &&
    node.attrs?.type === 'user_button' &&
    node.attrs?.action === 'profile' &&
    node.attrs?.style !== 'button'
  ) {
    // Only convert to dropdown if we have a theme and primary theme
    if (site?.theme_rules && site?.primary_theme) {
      node.attrs.style = 'dropdown';
      node.attrs.href = undefined;
      node.attrs.showWhen = 'all_logged_in';
    } else if (node.attrs?.style === undefined) {
      // If we don't have a theme and the style is undefined, set it to button
      node.attrs.style = 'button';
      node.attrs.href = undefined;
      node.attrs.showWhen = 'all_logged_in';
    }
  }

  // Recursively check children
  if ('content' in node && node.content && Array.isArray(node.content)) {
    node.content.forEach((childNode) => {
      applyUserButtonDropdown(childNode, site);
    });
  }
};

const NavbarSerializerV2 = ({ site, page }: NavbarSerializerProps) => {
  const { navbar: navbarData, navbar_uses_html: navbarUsesHtml } = site.viewable_site_version;

  if (navbarUsesHtml && navbarData) {
    return serializePageNode(navbarData as NodeContent, 'navbar');
  }

  if (!navbarData) {
    return null;
  }

  const firstSectionBackgroundColor = (
    (page?.viewable_page_version?.content as unknown as { content: SerializableNode[] })
      ?.content?.[0] as TSectionElement
  )?.attrs?.backgroundColor;

  if (navbarData.type === 'doc') {
    const typedNavbar = navbarData as NavbarDocument;
    const desktopData = typedNavbar?.attrs?.data?.desktop;
    const mobileData = typedNavbar?.attrs?.data?.mobile;

    if (!desktopData && !mobileData) {
      return null;
    }

    if (desktopData) {
      desktopData.attrs.firstSectionBackgroundColor = firstSectionBackgroundColor;
      applyUserButtonDropdown(desktopData, site);
    }
    if (mobileData) {
      mobileData.attrs.firstSectionBackgroundColor = firstSectionBackgroundColor;
      applyUserButtonDropdown(mobileData, site);
    }

    return (
      <>
        {desktopData && <div className={NavbarStylesV2.navbarDesktopView}>{serializeNode(desktopData, 'desktop')}</div>}
        {mobileData && <div className={NavbarStylesV2.navbarMobileView}>{serializeNode(mobileData, 'mobile')}</div>}
      </>
    );
  }

  // Navbar v1 JSON, migrate and construct device specific view
  const migratedData = navbarV2Migrator(navbarData);
  const mobileView = constructMobileView(migratedData);

  applyUserButtonDropdown(migratedData, site);
  applyUserButtonDropdown(mobileView, site);

  return (
    <>
      <div className={NavbarStylesV2.navbarDesktopView}>{serializeNode(migratedData, 'desktop')}</div>
      <div className={NavbarStylesV2.navbarMobileView}>{serializeNode(mobileView, 'mobile')}</div>
    </>
  );
};

export default NavbarSerializerV2;
